import { Formik } from 'formik'
import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import {
  FilterHeader,
  FilterWrapper,
  TagsWrapper,
  FiltersWrapper,
  FilterToggle,
  FiltersContainer,
  FiltersSubmit
} from 'components/Filters'
import { EVehiclePlateType, IVehiclesFilters } from 'features/Vehicles/types'
import { Tag } from 'components/v2/Tag'
import { isBoolean, isEmpty, keys } from 'lodash'
import { Input } from 'components/Input'
import { DriverAutocomplete } from 'features/Drivers/components/DriverAutocomplete'
import { IDriverAutocomplete } from 'features/Drivers/components/MultiDriverAutocomplete'
import { PartnerAutocomplete } from 'features/Partners/components/PartnerAutocomplete'
import { Select } from 'components/v2/Select'
import { TBooleanStr, stringIsBoolean, stringToBoolean } from 'utils/boolean'
import { GTTVehicleTemplateTypeInput } from 'gql/types.generated'
import { SelectState } from 'features/Cities/components/SelectState'
import { COUNTRY_CODES } from 'utils/countries'
import { STATES } from 'utils/states'
import { VehicleAutocomplete } from 'features/Vehicles/components/VehicleAutocomplete'
import { getVehicleLabelWithExternalId } from 'features/Vehicles/utils'
import { TVehicleTemplateTypeInput } from 'gql/generated/graphql'

const VEHICLE_TYPES_LIST = [
  TVehicleTemplateTypeInput.Bus,
  TVehicleTemplateTypeInput.Sedan,
  TVehicleTemplateTypeInput.Suv,
  TVehicleTemplateTypeInput.Van
]

const PLATE_TYPES_LIST = [
  EVehiclePlateType.EmployeeTransport,
  EVehiclePlateType.Federal,
  EVehiclePlateType.Load,
  EVehiclePlateType.Local,
  EVehiclePlateType.Other,
  EVehiclePlateType.Pass,
  EVehiclePlateType.PublicTransport,
  EVehiclePlateType.Test
]

const messages = defineMessages({
  vehiclePlaceholder: {
    id: 'vehicles.filters.vehicle.placeholder',
    defaultMessage: 'Select vehicle'
  },
  externalIdPlaceholder: {
    id: 'vehicles.filters.externalId.placeholder',
    defaultMessage: 'Enter partner external ID'
  },
  makePlaceholder: {
    id: 'vehicles.filters.make.placeholder',
    defaultMessage: 'Enter make'
  },
  platePlaceholder: {
    id: 'vehicles.filters.plate.placeholder',
    defaultMessage: 'Enter plate'
  },
  selectDriver: {
    id: 'vehicles.filters.selectDriver',
    defaultMessage: 'Select driver'
  },
  selectPartner: {
    id: 'vehicles.filters.selectPartner',
    defaultMessage: 'Select partner'
  },
  offerStatusPlaceholder: {
    id: 'vehicles.filters.offerStatus.placeholder',
    defaultMessage: 'Select offer status'
  },
  isAssigned: {
    id: 'vehicles.filters.isAssigned',
    defaultMessage: 'Assigned'
  },
  isNotAssigned: {
    id: 'vehicles.filters.isNotAssigned',
    defaultMessage: 'Not assigned'
  },
  statusPlaceholder: {
    id: 'vehicles.filters.status.placeholder',
    defaultMessage: 'Select status'
  },
  active: {
    id: 'vehicles.filters.active',
    defaultMessage: 'Active'
  },
  inactive: {
    id: 'vehicles.filters.inactive',
    defaultMessage: 'Inactive'
  },
  selectType: {
    id: 'vehicles.filters.selectType',
    defaultMessage: 'Select vehicle type'
  },
  selectPlaceholder: {
    id: 'vehicles.filters.selectPlaceholder',
    defaultMessage: 'Select an option'
  },
  selectPlateType: {
    id: 'vehicles.filters.selectPlateType',
    defaultMessage: 'Select plate type'
  },
  vinPlaceholder: {
    id: 'vehicles.filters.vin.placeholder',
    defaultMessage: 'Enter VIN'
  }
})

interface IVehiclesFiltersProps {
  filters: IVehiclesFilters
  onSubmit: (values: IVehiclesFilters) => void
}

const defaultValues: IVehiclesFilters = {
  vehicle: {
    id: '',
    name: '',
    externalID: ''
  },
  externalId: '',
  make: '',
  plate: '',
  driver: {
    id: '',
    name: ''
  },
  partner: {
    id: '',
    name: ''
  },
  isAssigned: null,
  hasDriver: null,
  isEnabled: null,
  type: null,
  plateType: null,
  registrationState: null,
  vin: ''
}

export const VehiclesFilters = ({ filters, onSubmit }: IVehiclesFiltersProps): ReactElement => {
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }
  const initialValues: IVehiclesFilters = {
    vehicle: filters.vehicle || defaultValues.vehicle,
    driver: filters.driver || defaultValues.driver,
    partner: filters.partner || defaultValues.partner,
    externalId: filters.externalId || defaultValues.externalId,
    make: filters.make || defaultValues.make,
    plate: filters.plate || defaultValues.plate,
    hasDriver: isBoolean(filters.hasDriver) ? filters.hasDriver : defaultValues.hasDriver,
    isAssigned: isBoolean(filters.isAssigned) ? filters.isAssigned : defaultValues.isAssigned,
    isEnabled: isBoolean(filters.isEnabled) ? filters.isEnabled : defaultValues.isEnabled,
    type: filters.type || defaultValues.type,
    plateType: filters.plateType || defaultValues.plateType,
    registrationState: filters.registrationState || defaultValues.registrationState,
    vin: filters.vin || defaultValues.vin
  }
  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { handleSubmit, handleChange, setFieldValue, values } = props

        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          setFieldValue(id, defaultValues[id])
          onSubmit({
            ...values,
            [id]: defaultValues[id]
          })
        }

        const handleStateChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
          const state = STATES.find((state) => state.code === value) || defaultValues.registrationState
          setFieldValue('registrationState', state)
          onSubmit({
            ...values,
            registrationState: state
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey) => {
                      let label, value
                      const filterValue = filters[filterKey as keyof typeof filters]
                      const hasValue = typeof filterValue === 'boolean' || !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'vehicle':
                          label = <FormattedMessage id="dictionary.vehicle" defaultMessage="Vehicle" />
                          value = (filterValue as IVehiclesFilters['vehicle']).name
                          break
                        case 'externalId':
                          label = (
                            <FormattedMessage
                              id="vehicles.filters.externalId.label"
                              defaultMessage="Partner external id"
                            />
                          )
                          value = filterValue
                          break
                        case 'vin':
                          label = <FormattedMessage id="vehicles.filters.vin.label" defaultMessage="VIN" />
                          value = filterValue
                          break
                        case 'driver':
                          label = <FormattedMessage id="dictionary.driver" defaultMessage="Driver" />
                          value = (filterValue as IVehiclesFilters['driver']).name
                          break
                        case 'partner':
                          label = <FormattedMessage id="dictionary.partner" defaultMessage="Partner" />
                          value = (filterValue as IVehiclesFilters['partner']).name
                          break
                        case 'isAssigned':
                          label = <FormattedMessage id="dictionary.offerStatus" defaultMessage="Offer status" />
                          value = filterValue
                            ? intl.formatMessage(messages.isAssigned)
                            : intl.formatMessage(messages.isNotAssigned)
                          break
                        case 'hasDriver':
                          label = <FormattedMessage id="dictionary.hasDriver" defaultMessage="Driver assigned" />
                          value = filterValue
                            ? intl.formatMessage(messages.isAssigned)
                            : intl.formatMessage(messages.isNotAssigned)
                          break
                        case 'isEnabled':
                          label = <FormattedMessage id="dictionary.status" defaultMessage="Status" />
                          value = filterValue
                            ? intl.formatMessage(messages.active)
                            : intl.formatMessage(messages.inactive)
                          break
                        case 'type':
                          label = <FormattedMessage id="dictionary.type" defaultMessage="Type" />
                          value = filterValue
                          break
                        case 'make':
                          label = <FormattedMessage id="dictionary.make" defaultMessage="Make" />
                          value = filterValue
                          break
                        case 'plate':
                          label = <FormattedMessage id="dictionary.plate" defaultMessage="Plate" />
                          value = filterValue
                          break
                        case 'plateType':
                          label = <FormattedMessage id="dictionary.plateType" defaultMessage="Plate type" />
                          value = filterValue
                          break
                        case 'registrationState':
                          label = (
                            <FormattedMessage id="dictionary.registrationState" defaultMessage="Registration state" />
                          )
                          value = (filterValue as IVehiclesFilters['registrationState'])?.name
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof filters)}
                        >
                          {label}: {value as string}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <VehicleAutocomplete
                    id="vehicle.id"
                    label={<FormattedMessage id="vehicles.filters.vehicle.label" defaultMessage="Vehicle" />}
                    placeholder={intl.formatMessage(messages.vehiclePlaceholder)}
                    value={getVehicleLabelWithExternalId(values.vehicle)}
                    onChange={(vehicle): void => {
                      setFieldValue('vehicle', vehicle)
                      onSubmit({
                        ...values,
                        vehicle: {
                          id: vehicle?.id || defaultValues.vehicle.id,
                          name: vehicle?.name || defaultValues.vehicle.name,
                          externalID: vehicle?.externalID || defaultValues.vehicle.externalID
                        }
                      })
                    }}
                  />
                  <Input
                    name="externalId"
                    label={
                      <FormattedMessage id="vehicles.filters.externalId.label" defaultMessage="Parner external id" />
                    }
                    placeholder={intl.formatMessage(messages.externalIdPlaceholder)}
                    value={values.externalId}
                    onChange={handleChange}
                  />
                  <Input
                    name="vin"
                    label={<FormattedMessage id="vehicles.filters.vin.label" defaultMessage="VIN" />}
                    placeholder={intl.formatMessage(messages.vinPlaceholder)}
                    value={values.vin}
                    onChange={handleChange}
                  />
                  <DriverAutocomplete
                    id="driver.id"
                    label={<FormattedMessage id="vehicles.filters.driver.label" defaultMessage="Driver" />}
                    placeholder={intl.formatMessage(messages.selectDriver)}
                    value={values.driver.name}
                    onChange={(driver: IDriverAutocomplete): void => {
                      setFieldValue('driver', driver)
                      onSubmit({
                        ...values,
                        driver
                      })
                    }}
                  />
                  <PartnerAutocomplete
                    id="partner.id"
                    label={<FormattedMessage id="vehicles.filters.partner.label" defaultMessage="Partner" />}
                    value={values.partner.name}
                    placeholder={intl.formatMessage(messages.selectPartner)}
                    onChange={(partner): void => {
                      setFieldValue('partner', partner)
                      onSubmit({
                        ...values,
                        partner
                      })
                    }}
                  />
                  <Select
                    id="isEnabled"
                    value={isBoolean(values.isEnabled) ? String(values.isEnabled) : ''}
                    onChange={({ target: { value } }): void => {
                      if (stringIsBoolean(value)) {
                        setFieldValue('isEnabled', stringToBoolean(value as TBooleanStr))
                        onSubmit({
                          ...values,
                          isEnabled: stringToBoolean(value as TBooleanStr)
                        })
                      } else {
                        setFieldValue('isEnabled', null)
                        onSubmit({
                          ...values,
                          isEnabled: null
                        })
                      }
                    }}
                    label={<FormattedMessage id="vehicles.filters.status.label" defaultMessage="Status" />}
                  >
                    <option value="">{intl.formatMessage(messages.statusPlaceholder)}</option>
                    <option value="true">{intl.formatMessage(messages.active)}</option>
                    <option value="false">{intl.formatMessage(messages.inactive)}</option>
                  </Select>
                  <Select
                    id="isAssigned"
                    value={isBoolean(values.isAssigned) ? String(values.isAssigned) : ''}
                    onChange={({ target: { value } }): void => {
                      if (stringIsBoolean(value)) {
                        setFieldValue('isAssigned', stringToBoolean(value as TBooleanStr))
                        onSubmit({
                          ...values,
                          isAssigned: stringToBoolean(value as TBooleanStr)
                        })
                      } else {
                        setFieldValue('isAssigned', null)
                        onSubmit({
                          ...values,
                          isAssigned: null
                        })
                      }
                    }}
                    label={<FormattedMessage id="vehicles.filters.offerStatus.label" defaultMessage="Offer status" />}
                  >
                    <option value="">{intl.formatMessage(messages.offerStatusPlaceholder)}</option>
                    <option value="true">{intl.formatMessage(messages.isAssigned)}</option>
                    <option value="false">{intl.formatMessage(messages.isNotAssigned)}</option>
                  </Select>
                  <Select
                    id="type"
                    value={values.type || ''}
                    onChange={({ target: { value } }): void => {
                      setFieldValue('type', value as GTTVehicleTemplateTypeInput)
                      onSubmit({
                        ...values,
                        type: value as GTTVehicleTemplateTypeInput
                      })
                    }}
                    label={<FormattedMessage id="vehicles.filters.type.label" defaultMessage="Type" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectType)}</option>
                    {VEHICLE_TYPES_LIST.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Select>
                  <Select
                    id="hasDriver"
                    value={isBoolean(values.hasDriver) ? String(values.hasDriver) : ''}
                    onChange={({ target: { value } }): void => {
                      if (stringIsBoolean(value)) {
                        setFieldValue('hasDriver', stringToBoolean(value as TBooleanStr))
                        onSubmit({
                          ...values,
                          hasDriver: stringToBoolean(value as TBooleanStr)
                        })
                      } else {
                        setFieldValue('hasDriver', null)
                        onSubmit({
                          ...values,
                          hasDriver: null
                        })
                      }
                    }}
                    label={<FormattedMessage id="vehicles.filters.hasDriver.label" defaultMessage="Driver Assigned" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectPlaceholder)}</option>
                    <option value="true">
                      <FormattedMessage id="vehicles.filters.hasDriver.yes" defaultMessage="Assigned" />
                    </option>
                    <option value="false">
                      <FormattedMessage id="vehicles.filters.hasDriver.no" defaultMessage="Not assigned" />
                    </option>
                  </Select>
                  <Input
                    name="make"
                    label={<FormattedMessage id="vehicles.filters.make.label" defaultMessage="Make" />}
                    placeholder={intl.formatMessage(messages.makePlaceholder)}
                    value={values.make}
                    onChange={handleChange}
                  />
                  <Input
                    name="plate"
                    label={<FormattedMessage id="vehicles.filters.plate.label" defaultMessage="Plate" />}
                    placeholder={intl.formatMessage(messages.platePlaceholder)}
                    value={values.plate}
                    onChange={handleChange}
                  />
                  <Select
                    id="plateType"
                    value={values.plateType || ''}
                    onChange={({ target: { value } }): void => {
                      setFieldValue('plateType', value as EVehiclePlateType)
                      onSubmit({
                        ...values,
                        plateType: value as EVehiclePlateType
                      })
                    }}
                    label={<FormattedMessage id="vehicles.filters.plateType.label" defaultMessage="Plate type" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectPlateType)}</option>
                    {PLATE_TYPES_LIST.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Select>
                  <SelectState
                    id="registrationState.code"
                    value={values.registrationState?.code || ''}
                    onChange={handleStateChange}
                    country={COUNTRY_CODES.MEXICO}
                    label={
                      <FormattedMessage
                        id="vehicles.filters.registrationState.label"
                        defaultMessage="Registration state"
                      />
                    }
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
